
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import tradingEnquiryService from "../services/tradingEnquiryService";
import axiosClient from "../../../api/axiosClient";

const styles = {
  page: { padding: 24, minHeight: "100%", background: "#f5f6fa" },
  panel: {
    maxWidth: 1400, margin: "0 auto", background: "#fff",
    border: "1px solid #e5e7eb", borderRadius: 12, overflow: "hidden",
  },
  header: {
    padding: "20px 24px",
    background: "linear-gradient(90deg, #5f0cff, #7b2cff)",
    color: "#fff", display: "flex", alignItems: "center",
    justifyContent: "space-between", gap: 16, flexWrap: "wrap",
  },
  body: { padding: 24 },
  section: {
    border: "1px solid #e5e7eb", borderRadius: 9,
    marginBottom: 22, overflow: "hidden",
  },
  sectionHeader: {
    padding: "14px 18px", background: "#f9fafb",
    borderBottom: "1px solid #e5e7eb", fontWeight: 700,
    fontSize: 14, color: "#111827",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 18, padding: 20,
  },
  label: {
    display: "block", color: "#6b7280",
    fontSize: 12, marginBottom: 7,
  },
  value: {
    color: "#111827", fontSize: 13,
    fontWeight: 600, overflowWrap: "anywhere",
  },
  input: {
    width: "100%", boxSizing: "border-box",
    padding: "9px 10px", border: "1px solid #d1d5db",
    borderRadius: 6, fontSize: 13, background: "#fff",
    color: "#111827",
  },
  button: {
    padding: "9px 15px", borderRadius: 7,
    border: "1px solid #d1d5db", background: "#fff",
    color: "#374151", cursor: "pointer",
    fontWeight: 600, fontSize: 12,
  },
  primary: {
    padding: "9px 15px", borderRadius: 7,
    border: "1px solid #6d28d9", background: "#6d28d9",
    color: "#fff", cursor: "pointer",
    fontWeight: 600, fontSize: 12,
  },
  danger: {
    padding: "7px 11px", borderRadius: 6,
    border: "1px solid #fecaca", background: "#fff",
    color: "#b91c1c", cursor: "pointer",
    fontWeight: 600, fontSize: 12,
  },
  cell: {
    padding: "12px 14px", borderBottom: "1px solid #eee",
    textAlign: "left", whiteSpace: "nowrap",
    fontSize: 12, color: "#374151",
  },
  alert: { marginBottom: 18, padding: 12, borderRadius: 7, fontSize: 13 },
};


const formatDate = (value) => {
  if (!value) return "—";

  const raw = String(value).trim();

  // Handle YYYY-MM-DD or ISO date strings without timezone shifting.
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${day} ${new Date(
      Number(year),
      Number(month) - 1,
      Number(day)
    ).toLocaleDateString("en-GB", { month: "short" })} ${year}`;
  }

  // Handle API dates such as:
  // Sun, 25 Oct 2026 00:00:00 GMT
  const date = new Date(raw);

  if (Number.isNaN(date.getTime())) return "—";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};


const formatStatus = (value) =>
  String(value || "—")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const getStatusStyle = (status) => {
  const colors = {
    NEW: { background: "#ede9fe", color: "#5b21b6" },
    COSTING: { background: "#fef3c7", color: "#92400e" },
    STUDY: { background: "#e0f2fe", color: "#075985" },
    QUOTED: { background: "#dcfce7", color: "#166534" },
    WON: { background: "#d1fae5", color: "#065f46" },
    LOST: { background: "#fee2e2", color: "#b91c1c" },
  };

  return {
    display: "inline-block",
    borderRadius: 20,
    padding: "5px 10px",
    fontSize: 11,
    fontWeight: 700,
    ...(colors[String(status || "").toUpperCase()] || {
      background: "#f3f4f6",
      color: "#4b5563",
    }),
  };
};

const responseData = (response) => response?.data?.data ?? response?.data;

const getErrorMessage = (err, fallback) =>
  err?.response?.data?.message ||
  err?.response?.data?.error ||
  err?.message ||
  fallback;

const getCustomerId = (customer) =>
  customer?.id ?? customer?.business_partner_id ?? customer?.partner_id;

const getCustomerName = (customer) =>
  customer?.customer_name ||
  customer?.business_partner_name ||
  customer?.partner_name ||
  customer?.business_partner_display_name ||
  customer?.partner_display_name ||
  customer?.display_name ||
  customer?.customer ||
  customer?.partner ||
  customer?.bp_name ||
  customer?.name ||
  (getCustomerId(customer)
    ? `Customer #${getCustomerId(customer)}`
    : "Unknown Customer");

const getLookupCode = (item) =>
  item?.lookup_code ??
  item?.code ??
  item?.value ??
  item?.status_code ??
  item?.id ??
  "";

const getLookupLabel = (item) =>
  item?.lookup_name ||
  item?.lookup_description ||
  item?.description ||
  item?.name ||
  item?.label ||
  item?.display_name ||
  getLookupCode(item);

const getMaterialName = (material) =>
  material?.material_name ||
  material?.name ||
  `Raw Material #${material?.id}`;

function Field({ label, children }) {
  return (
    <div>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

function DetailField({ label, value }) {
  return (
    <div>
      <div style={styles.label}>{label}</div>
      <div style={styles.value}>
        {value === null || value === undefined || value === "" ? "—" : value}
      </div>
    </div>
  );
}

function MasterSelect({
  label,
  value,
  options,
  placeholder,
  onChange,
}) {
  const currentValue = String(value ?? "");

  const hasCurrentValue = options.some(
    (option) => String(getLookupCode(option)) === currentValue
  );

  return (
    <Field label={label}>
      <select
        style={styles.input}
        value={currentValue}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">{placeholder}</option>

        {currentValue && !hasCurrentValue && (
          <option value={currentValue}>{currentValue}</option>
        )}

        {options.map((option, index) => {
          const code = String(getLookupCode(option));

          return (
            <option key={`${code}-${index}`} value={code}>
              {getLookupLabel(option)}
            </option>
          );
        })}
      </select>
    </Field>
  );
}

export default function TradingEnquiryDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [enquiry, setEnquiry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  const [editing, setEditing] = useState(false);
  const [headerForm, setHeaderForm] = useState({});
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const [lookups, setLookups] = useState({
    enquiryType: [],
    priority: [],
    industry: [],
    application: [],
  });

  const [transitions, setTransitions] = useState([]);
  const [transitionLoading, setTransitionLoading] = useState(false);

  const [materialSearch, setMaterialSearch] = useState("");
  const [materialOptions, setMaterialOptions] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [newQuantity, setNewQuantity] = useState("1");
  const [newDescription, setNewDescription] = useState("");
  const [addingItem, setAddingItem] = useState(false);
  const [itemBusyId, setItemBusyId] = useState(null);

  const currentStatus = String(enquiry?.status_code || "").toUpperCase();
  const isNew = currentStatus === "NEW";
  const isCosting = currentStatus === "COSTING";
  const isQuoted = currentStatus === "QUOTED";
  const items = Array.isArray(enquiry?.items) ? enquiry.items : [];

  const loadEnquiry = useCallback(async (showLoader = true) => {
    if (showLoader) setLoading(true);
    setError("");

    try {
      const response = await tradingEnquiryService.getEnquiry(id);
      const data = responseData(response);

      console.log("Enquiry date from API:", data?.enquiry_date);
      console.log("Expected response date from API:", data?.expected_response_date);

      if (!data?.id) {
        setEnquiry(null);
        setError("Trading enquiry was not found.");
        return null;
      }

      setEnquiry(data);
      return data;
    } catch (err) {
      setEnquiry(null);
      setError(
        getErrorMessage(err, "Unable to load Trading enquiry details.")
      );
      return null;
    } finally {
      if (showLoader) setLoading(false);
    }
  }, [id]);

  const loadTransitions = useCallback(async () => {
    setTransitionLoading(true);

    try {
      const response = await tradingEnquiryService.getAllowedTransitions(id);
      const data = responseData(response);

      setTransitions(
        Array.isArray(data?.transitions)
          ? data.transitions
          : Array.isArray(data)
            ? data
            : []
      );
    } catch (err) {
      setTransitions([]);
      setError(getErrorMessage(err, "Unable to load status transitions."));
    } finally {
      setTransitionLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadEnquiry();
  }, [loadEnquiry]);

  useEffect(() => {
    let active = true;

    const loadLookups = async () => {
      try {
        const response = await axiosClient.get("/api/ppe/masters/lookup", {
          params: {
            "types[]": [
              "ENQUIRY_TYPE",
              "FMS_PRIORITY",
              "INDUSTRY",
              "APPLICATION_TYPE",
            ],
          },
        });

        if (!active) return;

        const data = response.data?.data || {};

        setLookups({
          enquiryType: Array.isArray(data.ENQUIRY_TYPE)
            ? data.ENQUIRY_TYPE
            : [],
          priority: Array.isArray(data.FMS_PRIORITY)
            ? data.FMS_PRIORITY
            : [],
          industry: Array.isArray(data.INDUSTRY)
            ? data.INDUSTRY
            : [],
          application: Array.isArray(data.APPLICATION_TYPE)
            ? data.APPLICATION_TYPE
            : [],
        });
      } catch (err) {
        if (active) {
          console.error("Trading enquiry lookup loading failed:", err);
          setError(
            getErrorMessage(err, "Unable to load enquiry master values.")
          );
        }
      }
    };

    loadLookups();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (enquiry?.id) loadTransitions();
  }, [enquiry?.id, enquiry?.status_code, loadTransitions]);

  const beginEdit = () => {
    setError("");
    setMessage("");

    setHeaderForm({
      business_partner_id: enquiry.business_partner_id ?? "",
      enquiry_date: String(enquiry.enquiry_date || "").slice(0, 10),
      expected_response_date: String(
        enquiry.expected_response_date || ""
      ).slice(0, 10),
      priority_code: enquiry.priority_code || "",
      enquiry_type_code: enquiry.enquiry_type_code || "",
      industry_code: enquiry.industry_code || "",
      application_type_code: enquiry.application_type_code || "",
      requirement_description: enquiry.requirement_description || "",
      remarks: enquiry.remarks || "",
    });

    const customerId = enquiry.business_partner_id;
    const customerName =
      enquiry.customer_name ||
      enquiry.business_partner_name ||
      enquiry.partner_name ||
      "";

    setCustomerSearch(customerName);
    setSelectedCustomer(
      customerId
        ? {
            id: customerId,
            customer_name: customerName,
          }
        : null
    );

    setCustomerOptions([]);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setCustomerOptions([]);
    setSelectedCustomer(null);
    setCustomerSearch("");
    setError("");
  };

  const updateHeaderField = (field, value) => {
    setHeaderForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  };

  const searchCustomers = async (value) => {
    setCustomerSearch(value);
    setSelectedCustomer(null);
    setHeaderForm((previous) => ({
      ...previous,
      business_partner_id: "",
    }));
    setCustomerOptions([]);

    if (value.trim().length < 2) return;

    try {
      const response = await tradingEnquiryService.searchCustomers(
        value.trim()
      );
      const data = responseData(response);

      const options = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
          ? data.items
          : [];

      setCustomerOptions(options);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to search customers."));
    }
  };

  const chooseCustomer = (customer) => {
    const customerId = getCustomerId(customer);

    if (!customerId) {
      setError("The customer search result does not contain a customer ID.");
      return;
    }

    setSelectedCustomer({
      ...customer,
      id: customerId,
    });
    setCustomerSearch(getCustomerName(customer));
    setHeaderForm((previous) => ({
      ...previous,
      business_partner_id: customerId,
    }));
    setCustomerOptions([]);
    setError("");
  };

  const saveHeader = async (event) => {
    event.preventDefault();

    if (!isNew || saving) return;

    setError("");
    setMessage("");

    const customerId =
      selectedCustomer?.id || headerForm.business_partner_id;

    if (!customerId) {
      setError("Select a customer before saving.");
      return;
    }

    if (!headerForm.enquiry_date) {
      setError("Enquiry date is required.");
      return;
    }

    setSaving(true);

    try {
      await tradingEnquiryService.updateEnquiry(id, {
        ...headerForm,
        business_partner_id: customerId,
      });

      setEditing(false);
      setMessage("Trading enquiry updated.");
      await loadEnquiry(false);
    } catch (err) {
      setError(
        getErrorMessage(err, "Unable to update Trading enquiry.")
      );
    } finally {
      setSaving(false);
    }
  };

  const deleteEnquiry = async () => {
    if (!isNew || deleting) return;

    const confirmed = window.confirm(
      `Delete Trading enquiry ${
        enquiry.enquiry_no || `#${id}`
      } and its items? This cannot be undone.`
    );

    if (!confirmed) return;

    setDeleting(true);
    setError("");
    setMessage("");

    try {
      await tradingEnquiryService.deleteEnquiry(id);

      navigate("/trading/enquiries", {
        replace: true,
        state: { message: "Trading enquiry deleted." },
      });
    } catch (err) {
      setError(getErrorMessage(err, "Unable to delete Trading enquiry."));
    } finally {
      setDeleting(false);
    }
  };

  // The costing module will be connected here.
  // Do not change the enquiry status just because the button was clicked.
  
  const startCosting = async () => {
    if (changingStatus || !enquiry?.id) return;

    if (isNew && items.length === 0) {
      setError("Add at least one Raw Material before starting costing.");
      return;
    }

    if (
      !window.confirm(
        isNew
          ? `Move enquiry ${enquiry.enquiry_no || `#${id}`} to COSTING and create its draft costing?`
          : `Open or create the draft costing for ${enquiry.enquiry_no || `#${id}`}?`
      )
    ) {
      return;
    }

    setChangingStatus(true);
    setError("");
    setMessage("");

    try {
      // Change the enquiry status first, only when it is NEW.
      if (isNew) {
        await tradingEnquiryService.changeEnquiryStatus(id, "COSTING");
      }

      // Create the draft costing, or retrieve the existing current costing.
      const response = await axiosClient.post(
        `/api/trading/costings/start/${id}`
      );

      const costing = responseData(response);

      if (!costing?.id) {
        throw new Error(
          "The costing endpoint did not return a costing record."
        );
      }

      setMessage(
        `Draft costing ${costing.costing_no || `#${costing.id}`} is ready.`
      );

      await loadEnquiry(false);
      await loadTransitions();

      // The costing detail page will be connected in the next step.
      navigate("/trading/costings", {
        state: {
          message: `Draft costing ${costing.costing_no || `#${costing.id}`} is ready.`,
        },
      });
    } catch (err) {
      setError(
        getErrorMessage(err, "Unable to start or open Trading costing.")
      );
      await loadEnquiry(false);
    } finally {
      setChangingStatus(false);
    }
  };


  // The quotation module will be connected here.
  // The enquiry must not become QUOTED just because a quotation is requested.
  const createQuotation = () => {
    setError("");
    setMessage(
      "The Trading Quotation screen has not been implemented yet. No status has been changed."
    );
  };

  const changeStatus = async (newStatus) => {
    if (changingStatus) return;

    const normalizedStatus = String(newStatus || "").toUpperCase();

    // QUOTED must be set through the actual quotation lifecycle.
    // This page only allows the customer outcome transitions.
    if (!isQuoted || !["WON", "LOST"].includes(normalizedStatus)) {
      setError(
        "This status change is not available here. Use the relevant Trading workflow action."
      );
      return;
    }

    const label = formatStatus(newStatus);

    if (
      !window.confirm(
        `Change enquiry status from ${formatStatus(
          enquiry.status_code
        )} to ${label}?`
      )
    ) {
      return;
    }

    setChangingStatus(true);
    setError("");
    setMessage("");

    try {
      await tradingEnquiryService.changeEnquiryStatus(id, newStatus);
      setMessage(`Enquiry status changed to ${label}.`);
      await loadEnquiry(false);
      await loadTransitions();
    } catch (err) {
      setError(getErrorMessage(err, "Unable to change enquiry status."));
    } finally {
      setChangingStatus(false);
    }
  };

  const searchRawMaterials = async (value) => {
    setMaterialSearch(value);
    setSelectedMaterial(null);
    setMaterialOptions([]);

    if (value.trim().length < 2) return;

    try {
      const response = await tradingEnquiryService.searchRawMaterials(
        value.trim()
      );
      const data = responseData(response);

      setMaterialOptions(
        Array.isArray(data)
          ? data
          : Array.isArray(data?.items)
            ? data.items
            : []
      );
    } catch (err) {
      setError(getErrorMessage(err, "Unable to search Raw Materials."));
    }
  };

  const addItem = async () => {
    if (!isNew || addingItem) return;

    setError("");
    setMessage("");

    if (!selectedMaterial?.id) {
      setError("Search for and select a Raw Material first.");
      return;
    }

    const quantity = Number(newQuantity);

    if (!Number.isFinite(quantity) || quantity <= 0) {
      setError("Quantity must be greater than zero.");
      return;
    }

    if (
      items.some(
        (item) =>
          String(item.raw_material_id) ===
          String(selectedMaterial.id)
      )
    ) {
      setError("This Raw Material is already in the enquiry.");
      return;
    }

    setAddingItem(true);

    try {
      await tradingEnquiryService.addEnquiryItem(id, {
        raw_material_id: selectedMaterial.id,
        uom_id:
          selectedMaterial.issue_uom_id ||
          selectedMaterial.base_uom_id ||
          undefined,
        quantity: String(quantity),
        item_description:
          newDescription.trim() ||
          selectedMaterial.material_name ||
          "",
      });

      setMaterialSearch("");
      setMaterialOptions([]);
      setSelectedMaterial(null);
      setNewQuantity("1");
      setNewDescription("");
      setMessage("Raw Material added.");
      await loadEnquiry(false);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to add Raw Material."));
    } finally {
      setAddingItem(false);
    }
  };

  const editItem = async (item) => {
    if (!isNew) return;

    const quantityValue = window.prompt(
      "Enter quantity:",
      String(item.quantity ?? "1")
    );

    if (quantityValue === null) return;

    const quantity = Number(quantityValue);

    if (!Number.isFinite(quantity) || quantity <= 0) {
      setError("Quantity must be greater than zero.");
      return;
    }

    const descriptionValue = window.prompt(
      "Enter item description:",
      item.item_description || ""
    );

    if (descriptionValue === null) return;

    setItemBusyId(item.id);
    setError("");
    setMessage("");

    try {
      await tradingEnquiryService.updateEnquiryItem(id, item.id, {
        quantity: String(quantity),
        item_description: descriptionValue,
      });

      setMessage("Enquiry item updated.");
      await loadEnquiry(false);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to update enquiry item."));
    } finally {
      setItemBusyId(null);
    }
  };

  const deleteItem = async (item) => {
    if (!isNew || itemBusyId) return;

    if (
      !window.confirm(
        `Remove ${
          item.material_name || "this Raw Material"
        } from the enquiry?`
      )
    ) {
      return;
    }

    setItemBusyId(item.id);
    setError("");
    setMessage("");

    try {
      await tradingEnquiryService.deleteEnquiryItem(id, item.id);
      setMessage("Enquiry item deleted.");
      await loadEnquiry(false);
    } catch (err) {
      setError(getErrorMessage(err, "Unable to delete enquiry item."));
    } finally {
      setItemBusyId(null);
    }
  };

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.panel}>
          <div style={styles.body}>Loading Trading enquiry...</div>
        </div>
      </div>
    );
  }

  if (error && !enquiry) {
    return (
      <div style={styles.page}>
        <div style={styles.panel}>
          <div style={styles.header}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700 }}>
                Trading Enquiry
              </div>
              <div style={{ fontSize: 12, marginTop: 5 }}>
                Enquiry details
              </div>
            </div>

            <button
              type="button"
              style={styles.button}
              onClick={() => navigate("/trading/enquiries")}
            >
              Back to List
            </button>
          </div>

          <div
            role="alert"
            style={{
              ...styles.alert,
              margin: 24,
              background: "#fef2f2",
              color: "#b91c1c",
            }}
          >
            {error}
          </div>
        </div>
      </div>
    );
  }

  if (!enquiry) return null;

  return (
    <div style={styles.page}>
      <div style={styles.panel}>
        <div style={styles.header}>
          <div>
            <div style={{ fontSize: 21, fontWeight: 700 }}>
              {enquiry.enquiry_no || "Trading Enquiry"}
            </div>
            <div style={{ fontSize: 12, marginTop: 5, opacity: 0.9 }}>
              Customer requirement details
            </div>
          </div>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              style={styles.button}
              onClick={() => loadEnquiry()}
            >
              Refresh
            </button>

            {isNew && !editing && (
              <>
                <button
                  type="button"
                  style={styles.button}
                  onClick={beginEdit}
                >
                  Edit Enquiry
                </button>

                <button
                  type="button"
                  style={styles.danger}
                  onClick={deleteEnquiry}
                  disabled={deleting}
                >
                  {deleting ? "Deleting..." : "Delete Enquiry"}
                </button>
              </>
            )}

            <button
              type="button"
              style={styles.button}
              onClick={() => navigate("/trading/enquiries")}
            >
              Back to List
            </button>
          </div>
        </div>

        <div style={styles.body}>
          {error && (
            <div
              role="alert"
              style={{
                ...styles.alert,
                background: "#fef2f2",
                color: "#b91c1c",
              }}
            >
              {error}
            </div>
          )}

          {message && (
            <div
              role="status"
              style={{
                ...styles.alert,
                background: "#f0fdf4",
                color: "#166534",
              }}
            >
              {message}
            </div>
          )}

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              flexWrap: "wrap",
              marginBottom: 20,
            }}
          >
            <div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                Enquiry Number
              </div>
              <div
                style={{
                  fontSize: 19,
                  fontWeight: 750,
                  color: "#5f0cff",
                  marginTop: 4,
                }}
              >
                {enquiry.enquiry_no || "—"}
              </div>
            </div>

            <span style={getStatusStyle(enquiry.status_code)}>
              {formatStatus(enquiry.status_code)}
            </span>
          </div>

          {editing ? (
            <form onSubmit={saveHeader}>
              <div style={styles.section}>
                <div style={styles.sectionHeader}>Edit Enquiry Details</div>

                <div style={styles.grid}>
                  <Field label="Customer">
                    <input
                      style={styles.input}
                      value={customerSearch}
                      onChange={(event) =>
                        searchCustomers(event.target.value)
                      }
                      placeholder="Search customer..."
                      autoComplete="off"
                    />

                    {customerOptions.length > 0 && (
                      <div
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: 6,
                          marginTop: 4,
                          maxHeight: 180,
                          overflowY: "auto",
                          background: "#fff",
                        }}
                      >
                        {customerOptions.map((customer, index) => {
                          const customerId = getCustomerId(customer);

                          return (
                            <button
                              key={customerId ?? index}
                              type="button"
                              onClick={() => chooseCustomer(customer)}
                              style={{
                                display: "block",
                                width: "100%",
                                padding: 10,
                                textAlign: "left",
                                border: 0,
                                borderBottom: "1px solid #eee",
                                background: "#fff",
                                cursor: "pointer",
                              }}
                            >
                              {getCustomerName(customer)}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {selectedCustomer && (
                      <div
                        style={{
                          fontSize: 11,
                          color: "#166534",
                          marginTop: 5,
                        }}
                      >
                        Selected: {getCustomerName(selectedCustomer)}
                      </div>
                    )}
                  </Field>

                  <Field label="Enquiry Date">
                    <input
                      type="date"
                      style={styles.input}
                      value={headerForm.enquiry_date || ""}
                      onChange={(event) =>
                        updateHeaderField("enquiry_date", event.target.value)
                      }
                      required
                    />
                  </Field>

                  <Field label="Expected Response Date">
                    <input
                      type="date"
                      style={styles.input}
                      value={headerForm.expected_response_date || ""}
                      onChange={(event) =>
                        updateHeaderField(
                          "expected_response_date",
                          event.target.value
                        )
                      }
                    />
                  </Field>

                  <MasterSelect
                    label="Priority"
                    value={headerForm.priority_code}
                    options={lookups.priority}
                    placeholder="Select Priority"
                    onChange={(value) =>
                      updateHeaderField("priority_code", value)
                    }
                  />

                  <MasterSelect
                    label="Enquiry Type"
                    value={headerForm.enquiry_type_code}
                    options={lookups.enquiryType}
                    placeholder="Select Enquiry Type"
                    onChange={(value) =>
                      updateHeaderField("enquiry_type_code", value)
                    }
                  />

                  <MasterSelect
                    label="Industry"
                    value={headerForm.industry_code}
                    options={lookups.industry}
                    placeholder="Select Industry"
                    onChange={(value) =>
                      updateHeaderField("industry_code", value)
                    }
                  />

                  <MasterSelect
                    label="Application Type"
                    value={headerForm.application_type_code}
                    options={lookups.application}
                    placeholder="Select Application Type"
                    onChange={(value) =>
                      updateHeaderField("application_type_code", value)
                    }
                  />

                  <Field label="Requirement Description">
                    <textarea
                      style={{
                        ...styles.input,
                        minHeight: 90,
                        resize: "vertical",
                      }}
                      value={headerForm.requirement_description || ""}
                      onChange={(event) =>
                        updateHeaderField(
                          "requirement_description",
                          event.target.value
                        )
                      }
                    />
                  </Field>

                  <Field label="Remarks">
                    <textarea
                      style={{
                        ...styles.input,
                        minHeight: 90,
                        resize: "vertical",
                      }}
                      value={headerForm.remarks || ""}
                      onChange={(event) =>
                        updateHeaderField("remarks", event.target.value)
                      }
                    />
                  </Field>
                </div>

                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                    padding: "0 20px 20px",
                  }}
                >
                  <button
                    type="button"
                    style={styles.button}
                    onClick={cancelEdit}
                    disabled={saving}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    style={styles.primary}
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </form>
          ) : (
            <div style={styles.section}>
              <div style={styles.sectionHeader}>Enquiry Details</div>

              <div style={styles.grid}>
                <DetailField
                  label="Enquiry Number"
                  value={enquiry.enquiry_no}
                />
                <DetailField
                  label="Customer"
                  value={
                    enquiry.customer_name ||
                    enquiry.business_partner_name ||
                    enquiry.partner_name
                  }
                />
                <DetailField
                  label="Customer ID"
                  value={enquiry.business_partner_id}
                />
                <DetailField
                  label="Enquiry Date"
                  value={formatDate(enquiry.enquiry_date)}
                />
                <DetailField
                  label="Expected Response Date"
                  value={formatDate(enquiry.expected_response_date)}
                />
                <DetailField
                  label="Priority"
                  value={formatStatus(enquiry.priority_code)}
                />
                <DetailField
                  label="Status"
                  value={formatStatus(enquiry.status_code)}
                />
                <DetailField
                  label="Enquiry Type"
                  value={formatStatus(enquiry.enquiry_type_code)}
                />
                <DetailField
                  label="Industry"
                  value={formatStatus(enquiry.industry_code)}
                />
                <DetailField
                  label="Application Type"
                  value={formatStatus(enquiry.application_type_code)}
                />
              </div>
            </div>
          )}

          {!editing && (
            <div style={styles.section}>
              <div style={styles.sectionHeader}>Requirement & Remarks</div>

              <div style={{ padding: 20 }}>
                <div style={{ marginBottom: 20 }}>
                  <DetailField
                    label="Requirement Description"
                    value={enquiry.requirement_description}
                  />
                </div>

                <DetailField label="Remarks" value={enquiry.remarks} />
              </div>
            </div>
          )}

          <div style={styles.section}>
            <div
              style={{
                ...styles.sectionHeader,
                display: "flex",
                justifyContent: "space-between",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              <span>Required Raw Materials</span>
              <span style={{ color: "#6b7280", fontWeight: 500 }}>
                {items.length} item{items.length === 1 ? "" : "s"}
              </span>
            </div>

            <div style={{ overflowX: "auto" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: 12,
                }}
              >
                <thead>
                  <tr style={{ background: "#f9fafb" }}>
                    {[
                      "Line",
                      "Material Code",
                      "Material Name",
                      "Description",
                      "Quantity",
                      "UOM",
                      "Status",
                      ...(isNew ? ["Actions"] : []),
                    ].map((heading) => (
                      <th key={heading} style={styles.cell}>
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {items.map((item, index) => (
                    <tr key={item.id || item.item_seq_no || index}>
                      <td style={styles.cell}>
                        {item.item_seq_no || index + 1}
                      </td>

                      <td style={styles.cell}>
                        <strong>{item.material_code || "—"}</strong>
                      </td>

                      <td style={styles.cell}>
                        {item.material_name || "—"}
                      </td>

                      <td
                        style={{
                          ...styles.cell,
                          whiteSpace: "normal",
                          minWidth: 160,
                        }}
                      >
                        {item.item_description || "—"}
                      </td>

                      <td style={styles.cell}>{item.quantity ?? "—"}</td>

                      <td style={styles.cell}>
                        {item.uom_name || item.uom_code || "—"}
                      </td>

                      <td style={styles.cell}>
                        <span style={getStatusStyle(item.status_code)}>
                          {formatStatus(item.status_code)}
                        </span>
                      </td>

                      {isNew && (
                        <td style={styles.cell}>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              type="button"
                              style={styles.button}
                              disabled={itemBusyId === item.id}
                              onClick={() => editItem(item)}
                            >
                              Edit
                            </button>

                            <button
                              type="button"
                              style={styles.danger}
                              disabled={itemBusyId === item.id}
                              onClick={() => deleteItem(item)}
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}

                  {items.length === 0 && (
                    <tr>
                      <td
                        colSpan={isNew ? 8 : 7}
                        style={{
                          padding: 28,
                          textAlign: "center",
                          color: "#6b7280",
                        }}
                      >
                        No raw-material items were returned for this enquiry.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {isNew && (
              <div
                style={{
                  padding: 20,
                  borderTop: "1px solid #e5e7eb",
                }}
              >
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 13,
                    marginBottom: 14,
                  }}
                >
                  Add Raw Material
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: 12,
                    alignItems: "end",
                  }}
                >
                  <Field label="Search Raw Material">
                    <input
                      style={styles.input}
                      value={materialSearch}
                      onChange={(event) =>
                        searchRawMaterials(event.target.value)
                      }
                      placeholder="Enter material code or name..."
                    />

                    {materialOptions.length > 0 && (
                      <div
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: 6,
                          marginTop: 4,
                          maxHeight: 180,
                          overflowY: "auto",
                        }}
                      >
                        {materialOptions.map((material) => (
                          <button
                            key={material.id}
                            type="button"
                            onClick={() => {
                              setSelectedMaterial(material);
                              setMaterialSearch(
                                `${material.material_code || ""} ${getMaterialName(
                                  material
                                )}`.trim()
                              );
                              setMaterialOptions([]);
                            }}
                            style={{
                              display: "block",
                              width: "100%",
                              padding: 10,
                              textAlign: "left",
                              border: 0,
                              borderBottom: "1px solid #eee",
                              background: "#fff",
                              cursor: "pointer",
                            }}
                          >
                            <strong>{material.material_code || ""}</strong>{" "}
                            {getMaterialName(material)}
                          </button>
                        ))}
                      </div>
                    )}
                  </Field>

                  <Field label="Quantity">
                    <input
                      type="number"
                      min="0.000001"
                      step="any"
                      style={styles.input}
                      value={newQuantity}
                      onChange={(event) =>
                        setNewQuantity(event.target.value)
                      }
                    />
                  </Field>

                  <Field label="Item Description">
                    <input
                      style={styles.input}
                      value={newDescription}
                      onChange={(event) =>
                        setNewDescription(event.target.value)
                      }
                      placeholder="Optional description"
                    />
                  </Field>

                  <button
                    type="button"
                    style={styles.primary}
                    onClick={addItem}
                    disabled={addingItem}
                  >
                    {addingItem ? "Adding..." : "Add Item"}
                  </button>
                </div>

                {selectedMaterial && (
                  <div
                    style={{
                      fontSize: 12,
                      color: "#166534",
                      marginTop: 10,
                    }}
                  >
                    Selected: {selectedMaterial.material_code || ""}{" "}
                    {getMaterialName(selectedMaterial)}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={styles.section}>
            <div style={styles.sectionHeader}>Enquiry Workflow</div>

            <div style={{ padding: 20 }}>
              <div
                style={{
                  fontSize: 13,
                  color: "#4b5563",
                  marginBottom: 14,
                }}
              >
                Current status:{" "}
                <strong>{formatStatus(enquiry.status_code)}</strong>
              </div>

              {transitionLoading ? (
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  Loading available transitions...
                </div>
              
              ) : isNew ? (
                <button
                    type="button"
                    style={styles.primary}
                    onClick={startCosting}
                    disabled={changingStatus}
                >
                    {changingStatus ? "Starting Costing..." : "Start Costing"}
                </button>
                ) : isCosting ? (
                <button
                    type="button"
                    style={styles.primary}
                    onClick={startCosting}
                    disabled={changingStatus}
                >
                    {changingStatus ? "Opening Costing..." : "Open / Resume Costing"}
                </button>  

              ) : isQuoted ? (
                (() => {
                  const outcomeTransitions = transitions.filter((transition) =>
                    ["WON", "LOST"].includes(
                      String(transition.status_code || "").toUpperCase()
                    )
                  );

                  return outcomeTransitions.length > 0 ? (
                    <div
                      style={{
                        display: "flex",
                        gap: 10,
                        flexWrap: "wrap",
                      }}
                    >
                      {outcomeTransitions.map((transition) => (
                        <button
                          key={transition.status_code}
                          type="button"
                          style={
                            String(transition.status_code).toUpperCase() ===
                            "LOST"
                              ? styles.danger
                              : styles.primary
                          }
                          disabled={changingStatus}
                          onClick={() => changeStatus(transition.status_code)}
                        >
                          {changingStatus
                            ? "Updating..."
                            : `Change to ${formatStatus(
                                transition.status_code
                              )}`}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      No outcome transitions are configured.
                    </div>
                  );
                })()
              ) : (
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  No further status transitions are configured.
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 10,
            }}
          >
            <button
              type="button"
              style={styles.button}
              onClick={() => navigate("/trading/enquiries")}
            >
              Back to Enquiries
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
